import { main_url } from "./Common";

export const RECEIPT_FILE_ACCEPT =
  "image/jpeg,image/png,image/gif,image/webp,image/*,.pdf,application/pdf";

export function resolveAttachmentMediaUrl(value) {
  if (value == null || value === "") return "";

  if (typeof value === "object") {
    const candidate =
      value.url ??
      value.attachment_url ??
      value.receipt_url ??
      value.path ??
      value.file_path ??
      value.file ??
      value.name ??
      "";
    return resolveAttachmentMediaUrl(candidate);
  }

  const text = String(value).trim();
  if (!text) return "";
  if (/^(blob:|data:|https?:\/\/)/i.test(text)) return text;
  if (text.startsWith("//")) return `https:${text}`;
  if (text.startsWith("/")) return `${main_url()}${text}`;
  return `${main_url()}/${text}`;
}

function attachmentIdentityKey(value) {
  const url = resolveAttachmentMediaUrl(value);
  if (!url) return "";
  return url
    .replace(/^https?:\/\/[^/]+/i, "")
    .replace(/^\/+/, "")
    .toLowerCase();
}

function flattenAttachmentCandidates(value) {
  if (value == null || value === "") return [];

  if (Array.isArray(value)) {
    return value.flatMap((item) => flattenAttachmentCandidates(item));
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    if (
      (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
      (trimmed.startsWith("{") && trimmed.endsWith("}"))
    ) {
      try {
        return flattenAttachmentCandidates(JSON.parse(trimmed));
      } catch {
        return [trimmed];
      }
    }

    return [trimmed];
  }

  if (typeof value === "object") {
    return [value];
  }

  return [];
}

export function collectTransactionAttachmentUrls(record = {}) {
  const candidates = [
    ...flattenAttachmentCandidates(record.attachment_urls),
    ...flattenAttachmentCandidates(record.attachments),
    ...flattenAttachmentCandidates(record.attachment_files),
    ...flattenAttachmentCandidates(record.receipt_files),
    ...flattenAttachmentCandidates(record.finance_invoice_attachments),
    ...flattenAttachmentCandidates(record.transaction_attachments),
    record.attachment_url,
    record.receipt_url,
    record.attachment,
  ].filter((item) => item != null && item !== "");

  Object.entries(record).forEach(([key, value]) => {
    if (/^attachment_\d+$/i.test(key) && value != null && value !== "") {
      candidates.push(value);
    }
  });

  const urls = [];
  const seen = new Set();

  candidates.forEach((candidate) => {
    const url = resolveAttachmentMediaUrl(candidate);
    const identity = attachmentIdentityKey(url);
    if (!url || !identity || seen.has(identity)) return;
    seen.add(identity);
    urls.push(url);
  });

  return urls;
}

export function isReceiptPdfFile(file) {
  if (!file) return false;
  const type = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  return (
    type === "application/pdf" ||
    type === "application/x-pdf" ||
    name.endsWith(".pdf")
  );
}

export function isReceiptImageFile(file) {
  if (!file) return false;
  const type = String(file.type || "").toLowerCase();
  if (type.startsWith("image/")) return true;
  const name = String(file.name || "").toLowerCase();
  return /\.(jpe?g|png|gif|webp|bmp)$/i.test(name);
}

export function isAllowedReceiptFile(file) {
  return isReceiptImageFile(file) || isReceiptPdfFile(file);
}

export function buildReceiptPreviewUrl(file) {
  if (!isAllowedReceiptFile(file)) return "";
  return URL.createObjectURL(file);
}

/** Let axios set multipart boundary; instance default is application/json. */
export function buildMultipartFormDataRequestConfig(extraConfig = {}) {
  return {
    ...extraConfig,
    transformRequest: [
      (data, headers) => {
        if (typeof FormData !== "undefined" && data instanceof FormData) {
          delete headers["Content-Type"];
        }
        return data;
      },
      ...(Array.isArray(extraConfig.transformRequest)
        ? extraConfig.transformRequest
        : extraConfig.transformRequest
          ? [extraConfig.transformRequest]
          : []),
    ],
  };
}

export function buildFinanceInvoicePaymentFormData({  invoiceId,
  clientId,
  payment = {},
  receiptFile = null,
  receiptFiles = null,
}) {
  const formData = new FormData();

  formData.append("invoice_id", String(invoiceId));
  if (clientId) {
    formData.append("client_id", String(clientId));
  }

  formData.append("transaction_type", payment.transaction_type || "payment");
  formData.append("amount", String(payment.amount ?? ""));
  formData.append("payment_method", payment.payment_method || "cash");
  formData.append("transaction_date", payment.transaction_date || "");
  formData.append("reference", payment.reference || "");
  formData.append("description", payment.description || "Payment received");

  if (payment.finance_invoice_item_id) {
    formData.append(
      "finance_invoice_item_id",
      String(payment.finance_invoice_item_id),
    );
  }
  if (payment.commission_installment_id) {
    formData.append(
      "commission_installment_id",
      String(payment.commission_installment_id),
    );
  }
  if (payment.bonus_installment_id) {
    formData.append("bonus_installment_id", String(payment.bonus_installment_id));
  }
  if (payment.line_number) {
    formData.append("line_number", String(payment.line_number));
  }

  const files = Array.isArray(receiptFiles)
    ? receiptFiles.filter(Boolean)
    : receiptFile
      ? [receiptFile]
      : [];

  files.forEach((file, index) => {
    formData.append(`attachments[${index}]`, file);
    if (index === 0) {
      formData.append("attachment", file);
    }
  });

  return formData;
}

export function isFinanceInvoicePaymentApiSuccess(response) {
  return Boolean(
    response?.data?.success ||
      response?.data?.status ||
      response?.status === 200 ||
      response?.status === 201,
  );
}

export function getFinanceInvoicePaymentApiMessage(
  response,
  fallback = "Unable to record payment.",
) {
  return (
    response?.data?.message ||
    response?.data?.error ||
    response?.data?.msg ||
    fallback
  );
}

/**
 * Record multiple line-item payments in order (one API call per allocation).
 */
export async function recordFinanceInvoicePaymentsSequentially(
  entries = [],
  savePayment,
  { silentUntilLast = true } = {},
) {
  if (!Array.isArray(entries) || !entries.length || typeof savePayment !== "function") {
    return false;
  }

  for (let index = 0; index < entries.length; index += 1) {
    const entry = entries[index];
    const payment = entry?.payment || entry;
    const receiptFiles = entry?.receiptFiles || null;
    const receiptFile = receiptFiles?.[0] || entry?.receiptFile || null;
    const isLast = index === entries.length - 1;
    const saved = await savePayment(payment, receiptFile, receiptFiles, {
      silent: silentUntilLast && !isLast,
    });

    if (!saved) {
      return false;
    }
  }

  return true;
}

export function buildFinanceInvoiceReversePaymentPayload({
  transactionId,
  reason,
  amount,
}) {
  return {
    transaction_id: Number(transactionId),
    amount: Math.abs(Number(amount) || 0),
    reason: String(reason || "").trim(),
  };
}
